<script setup lang="ts">
import CustomButton from '@/components/CustomButton.vue';
import Link from '@/components/Link.vue';
import MarkdownText from '@/components/MarkdownText.vue';
import { useNotificationStore } from '@/stores/notification';
import type { ShortenedNotification } from '@/types/notification';
import { toDisplayDate } from '@/utils/date';
import { ellipsifyMarkdown } from '@/utils/text';
import { computed, ref } from 'vue';

const notificationStore = useNotificationStore();

const props = defineProps<{
    notification: ShortenedNotification;
}>();

const isExtraInfoDialogOpen = ref(false);

async function setUnread(): Promise<void> {
    await setUnreadAndRefresh();
    isExtraInfoDialogOpen.value = false;
}

async function setReadAndRefresh(): Promise<void> {
    await notificationStore.setRead(props.notification._id);
    await notificationStore.getForLoggedInEmployee();
}

async function setUnreadAndRefresh(): Promise<void> {
    await notificationStore.setUnread(props.notification._id);
    await notificationStore.getForLoggedInEmployee();
}

async function showExtraInfoDialog(): Promise<void> {
    isExtraInfoDialogOpen.value = true;
    await setReadAndRefresh();
}

async function notificationBodyClick(event: MouseEvent): Promise<void> {
    const target = event.target as HTMLElement;
    event.preventDefault();

    if (target.tagName === 'A') {
        await setReadAndRefresh();
    } else {
        await showExtraInfoDialog();
    }
}

const displayName = computed(() => {
    if (props.notification.createdBy == undefined) return 'Ongeldige naam...';
    if (typeof props.notification.createdBy == 'string') return 'Onbekend';
    return props.notification.createdBy?.fullName;
});
</script>

<template>
    <div class="notification mb-5 border rounded-lg">
        <div class="notification-header">
            <div class="d-flex justify-space-between align-center bg-main rounded-t-lg px-2">
                <Link
                    v-if="props.notification.createdBy"
                    :route-to="{
                        name: 'employee',
                        params: {
                            id: typeof props.notification?.createdBy === 'string' ? props.notification?.createdBy : props.notification.createdBy._id
                        }
                    }"
                    icon="person"
                    :displayText="displayName"
                    iconClass="text-white"
                    spanClass="text-white"
                />
                <p class="smaller-text ma-0 text-white" v-if="props.notification.createdOn != undefined">
                    {{ toDisplayDate(props.notification.createdOn, true) }}
                </p>
            </div>
        </div>
        <div class="notification-hover py-1" :class="props.notification.isRead == false && 'bg-grey-medium-dark'">
            <h5 class="px-3 notification-title" @click="showExtraInfoDialog">{{ props.notification.title }}</h5>
            <p class="small-text notification-body pa-3 pb-1" @click="notificationBodyClick">
                <MarkdownText
                    @click="notificationBodyClick"
                    :markdown="ellipsifyMarkdown(props.notification.message, 100)"
                    :newLineToBr="true"
                    :openLinksInNewTab="false"
                />
            </p>
        </div>
    </div>

    <v-dialog v-model="isExtraInfoDialogOpen" max-width="50vw">
        <v-card class="pa-2">
            <v-card-title class="d-flex justify-space-between align-center">
                {{ props.notification.title }}
                <v-icon icon="close" class="hover-main rounded-xl" @click="isExtraInfoDialogOpen = false" />
            </v-card-title>
            <v-card-text class="dialog-content">
                <MarkdownText :markdown="props.notification.message" :newLineToBr="true" :openLinksInNewTab="false" />
            </v-card-text>
            <v-card-actions>
                <CustomButton class="mr-auto ml-2" color="primary" text="Markeer als ongelezen" @click="setUnread" />
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<style scoped>
.small-text {
    font-size: 1rem;
}
.smaller-text {
    font-size: 0.8rem;
}

.notification-title,
.notification-body,
.notification-chip-holder {
    cursor: pointer;
}

.notification-hover {
    transition: background-color 0.2s;
    &:hover {
        background-color: var(--c-grey-light) !important;
    }
}
</style>
